/**
 * Contact custom field templates. Rules mirror the backend
 * (cloudtelephony_backend/src/helpers/contact-custom-fields.js).
 * Values are saved per contact in additional_details, keyed by field name.
 */

/** A contact form shows at most 20 fields; 10 are the fixed contact fields. */
export const MAX_CONTACT_FORM_FIELDS = 20;
export const BASE_CONTACT_FIELD_COUNT = 10;
export const MAX_CUSTOM_FIELDS = MAX_CONTACT_FORM_FIELDS - BASE_CONTACT_FIELD_COUNT;

export const FIELD_TYPES = ['String', 'Textarea', 'Integer', 'Float', 'Date', 'Boolean', 'Multi Select'] as const;
export type CustomFieldType = (typeof FIELD_TYPES)[number];

export const TYPE_LABELS: Record<CustomFieldType, string> = {
  String: 'Text',
  Textarea: 'Text Area',
  Integer: 'Numbers',
  Float: 'Decimal Values',
  Date: 'Date',
  Boolean: 'Checkbox',
  'Multi Select': 'Multi Select',
};

const NO_MIN_MAX_TYPES: CustomFieldType[] = ['String', 'Textarea', 'Boolean', 'Multi Select'];
export const hasMinMax = (type: CustomFieldType) => !NO_MIN_MAX_TYPES.includes(type);

export const TEMPLATE_NAME_REGEX = /^[a-zA-Z0-9 _-]*$/;
export const FIELD_NAME_REGEX = /^[a-zA-Z0-9 _\-?.,'/:&!%+#@]*$/;
export const MAX_NAME_LEN = 100;
const MAX_STRING_LEN = 255;
const MAX_TEXTAREA_LEN = 2000;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const RESERVED_FIELD_NAMES = new Set([
  'first name', 'last name', 'primary number', 'alternate number 1', 'alternate number 2',
  'street', 'city', 'state', 'country', 'company',
]);

export type CustomFieldDefault = string | number | boolean | string[] | null;

/** A field as stored on the template. */
export interface CustomField {
  field_name: string;
  field_type: CustomFieldType;
  is_required: boolean;
  field_default: CustomFieldDefault;
  field_min: number | string | null;
  field_max: number | string | null;
  options: string[] | null;
}

export interface CustomFieldTemplate {
  id: number;
  name: string;
  fields: CustomField[];
  /** Live contacts whose last saved template is this one. */
  contact_count?: number;
  created_at?: string;
  updated_at?: string;
}

/** Builder row: everything kept as strings/flags while editing. */
export interface BuilderField {
  field_name: string;
  field_type: CustomFieldType;
  is_required: boolean;
  enable_default: boolean;
  field_default: string;
  default_checked: boolean;
  enable_min: boolean;
  enable_max: boolean;
  field_min: string;
  field_max: string;
  options: string;
}

export const emptyBuilderField = (): BuilderField => ({
  field_name: '',
  field_type: 'String',
  is_required: false,
  enable_default: false,
  field_default: '',
  default_checked: false,
  enable_min: false,
  enable_max: false,
  field_min: '',
  field_max: '',
  options: '',
});

export function templateFieldToBuilder(f: CustomField): BuilderField {
  const isBool = f.field_type === 'Boolean';
  const def = f.field_default;
  return {
    field_name: f.field_name,
    field_type: f.field_type,
    is_required: !!f.is_required,
    enable_default: !isBool && def !== null && def !== undefined && def !== '',
    field_default: isBool || def === null || def === undefined ? '' : Array.isArray(def) ? def.join(', ') : String(def),
    default_checked: isBool && def === true,
    enable_min: f.field_min !== null && f.field_min !== undefined,
    enable_max: f.field_max !== null && f.field_max !== undefined,
    field_min: f.field_min === null || f.field_min === undefined ? '' : String(f.field_min),
    field_max: f.field_max === null || f.field_max === undefined ? '' : String(f.field_max),
    options: (f.options || []).join(', '),
  };
}

const splitOptions = (s: string) => [...new Set(s.split(',').map((o) => o.trim()).filter(Boolean))];

function parseBound(type: CustomFieldType, raw: string, label: string): { value: number | string } | { error: string } {
  const s = raw.trim();
  if (!s) return { error: `${label} is empty` };
  if (type === 'Date') return DATE_RE.test(s) ? { value: s } : { error: `${label} must be a date` };
  const n = Number(s);
  if (!Number.isFinite(n)) return { error: `${label} must be a number` };
  if (type === 'Integer' && !Number.isInteger(n)) return { error: `${label} must be a whole number` };
  return { value: n };
}

/** One builder row (template field or a field added on a contact) → stored field, or its first problem. */
function builderFieldToField(f: BuilderField, at: string): { field: CustomField } | { error: string } {
  const name = f.field_name.trim();
  if (!name) return { error: `${at}: field name is required` };
  if (name.length > MAX_NAME_LEN) return { error: `${at}: field name is too long` };
  if (!FIELD_NAME_REGEX.test(name)) return { error: `${at}: field name contains invalid characters` };
  if (RESERVED_FIELD_NAMES.has(name.toLowerCase())) return { error: `${at}: "${name}" is already a contact field` };

  const field: CustomField = {
    field_name: name,
    field_type: f.field_type,
    is_required: f.is_required,
    field_default: null,
    field_min: null,
    field_max: null,
    options: null,
  };

  if (f.field_type === 'Multi Select') {
    const options = splitOptions(f.options);
    if (!options.length) return { error: `${at}: add at least one option` };
    field.options = options;
  }

  if (hasMinMax(f.field_type)) {
    if (f.enable_min) {
      const b = parseBound(f.field_type, f.field_min, `${at}: min`);
      if ('error' in b) return b;
      field.field_min = b.value;
    }
    if (f.enable_max) {
      const b = parseBound(f.field_type, f.field_max, `${at}: max`);
      if ('error' in b) return b;
      field.field_max = b.value;
    }
    const min = field.field_min;
    const max = field.field_max;
    if (min !== null && max !== null && (f.field_type === 'Date' ? String(min) > String(max) : Number(min) > Number(max))) {
      return { error: `${at}: min cannot be greater than max` };
    }
  }

  if (f.field_type === 'Boolean') {
    field.field_default = f.default_checked;
  } else if (f.enable_default && f.field_default.trim()) {
    const err = valueError(field, f.field_type === 'Multi Select' ? splitOptions(f.field_default) : f.field_default);
    if (err) return { error: `${at}: default ${err}` };
    field.field_default = toStoredValue(field, f.field_type === 'Multi Select' ? splitOptions(f.field_default) : f.field_default);
  }
  return { field };
}

/** Builder rows → API payload, or the first problem found. */
export function builderToPayload(fields: BuilderField[]): { fields: CustomField[] } | { error: string } {
  if (!fields.length) return { error: 'Add at least one field' };
  if (fields.length > MAX_CUSTOM_FIELDS) {
    return { error: `A template can have at most ${MAX_CUSTOM_FIELDS} fields` };
  }
  const seen = new Set<string>();
  const out: CustomField[] = [];
  for (const [i, f] of fields.entries()) {
    const built = builderFieldToField(f, `Field #${i + 1}`);
    if ('error' in built) return built;
    const key = built.field.field_name.toLowerCase();
    if (seen.has(key)) return { error: `Duplicate field name: ${built.field.field_name}` };
    seen.add(key);
    out.push(built.field);
  }
  return { fields: out };
}

/** Value while a contact form is being edited. */
export type CustomValue = string | boolean | string[];
export type CustomValues = Record<string, CustomValue>;

const isBlank = (v: CustomValue | undefined) =>
  v === undefined || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0);

function valueError(field: CustomField, value: CustomValue): string | null {
  const type = field.field_type;
  if (type === 'Boolean') return null;
  if (type === 'Multi Select') {
    const picked = Array.isArray(value) ? value : [String(value)];
    const allowed = new Set((field.options || []).map((o) => o.toLowerCase()));
    const bad = picked.filter((o) => !allowed.has(o.toLowerCase()));
    return bad.length ? `has unknown option(s): ${bad.join(', ')}` : null;
  }
  const s = String(value).trim();
  if (type === 'Integer' || type === 'Float') {
    const n = Number(s);
    if (!Number.isFinite(n)) return 'must be a number';
    if (type === 'Integer' && !Number.isInteger(n)) return 'must be a whole number';
    if (field.field_min !== null && n < Number(field.field_min)) return `must be at least ${field.field_min}`;
    if (field.field_max !== null && n > Number(field.field_max)) return `must be at most ${field.field_max}`;
    return null;
  }
  if (type === 'Date') {
    if (!DATE_RE.test(s)) return 'must be a date';
    if (field.field_min !== null && s < String(field.field_min)) return `must be on or after ${field.field_min}`;
    if (field.field_max !== null && s > String(field.field_max)) return `must be on or before ${field.field_max}`;
    return null;
  }
  const limit = type === 'Textarea' ? MAX_TEXTAREA_LEN : MAX_STRING_LEN;
  return s.length > limit ? `must be at most ${limit} characters` : null;
}

/** Problem with a typed value, shown under its input as it is typed (blank is checked on save). */
export function fieldValueError(field: CustomField, value: CustomValue | undefined): string | null {
  if (field.field_type === 'Boolean' || isBlank(value)) return null;
  return valueError(field, value ?? '');
}

function toStoredValue(field: CustomField, value: CustomValue): CustomFieldDefault {
  if (field.field_type === 'Integer' || field.field_type === 'Float') return Number(String(value).trim());
  if (field.field_type === 'Multi Select') return Array.isArray(value) ? value : [String(value)];
  if (field.field_type === 'Boolean') return value === true;
  return String(value).trim();
}

/** Form values for a template: saved contact values first, then template defaults. */
export function initialCustomValues(
  template: CustomFieldTemplate | null | undefined,
  saved?: Record<string, unknown> | null,
): CustomValues {
  const out: CustomValues = {};
  for (const f of template?.fields || []) {
    const raw = saved && Object.prototype.hasOwnProperty.call(saved, f.field_name) ? saved[f.field_name] : f.field_default;
    if (f.field_type === 'Boolean') out[f.field_name] = raw === true;
    else if (f.field_type === 'Multi Select') out[f.field_name] = Array.isArray(raw) ? raw.map(String) : raw ? [String(raw)] : [];
    else out[f.field_name] = raw === null || raw === undefined ? '' : String(raw);
  }
  return out;
}

/** First validation problem in the form values, or null. */
export function validateCustomValues(template: CustomFieldTemplate | null | undefined, values: CustomValues): string | null {
  for (const f of template?.fields || []) {
    const v = values[f.field_name];
    if (f.field_type !== 'Boolean' && isBlank(v)) {
      if (f.is_required) return `${f.field_name} is required`;
      continue;
    }
    const err = valueError(f, v ?? '');
    if (err) return `${f.field_name} ${err}`;
  }
  return null;
}

/** Request body part: { custom_field_template_id, custom_fields } (blank fields sent as ''). */
export function customFieldsPayload(template: CustomFieldTemplate | null | undefined, values: CustomValues) {
  if (!template) return {};
  const custom_fields: Record<string, CustomFieldDefault | ''> = {};
  for (const f of template.fields) {
    const v = values[f.field_name];
    custom_fields[f.field_name] = f.field_type !== 'Boolean' && isBlank(v) ? '' : toStoredValue(f, v ?? '');
  }
  return { custom_field_template_id: template.id, custom_fields };
}

/** CSV header for a field, e.g. "Customer Type(Required)". */
export const customFieldHeader = (f: CustomField) => `${f.field_name}(${f.is_required ? 'Required' : 'Optional'})`;

function sampleCell(f: CustomField): string {
  if (f.field_default !== null && f.field_default !== undefined && f.field_default !== '') {
    if (Array.isArray(f.field_default)) return f.field_default.join(',');
    if (typeof f.field_default === 'boolean') return f.field_default ? 'Yes' : 'No';
    return String(f.field_default);
  }
  switch (f.field_type) {
    case 'Integer': return String(f.field_min ?? 1);
    case 'Float': return String(f.field_min ?? 1.5);
    case 'Date': return String(f.field_min ?? '2026-01-31');
    case 'Boolean': return 'Yes';
    case 'Multi Select': return (f.options || []).slice(0, 2).join(',');
    default: return '';
  }
}

/** Upload template columns and one sample row for the chosen template. */
export function withTemplateColumns(
  baseColumns: string[],
  baseSample: Record<string, string>[],
  template: CustomFieldTemplate | null | undefined,
): { columns: string[]; sample: Record<string, string>[] } {
  if (!template) return { columns: baseColumns, sample: baseSample };
  const extra = template.fields.map(customFieldHeader);
  const sample = baseSample.map((row) => {
    const next = { ...row };
    template.fields.forEach((f) => {
      next[customFieldHeader(f)] = sampleCell(f);
    });
    return next;
  });
  return { columns: [...baseColumns, ...extra], sample };
}

/** Short hint per type for the upload rules list. */
export function csvFormatHint(f: CustomField): string {
  switch (f.field_type) {
    case 'Integer': return 'whole number';
    case 'Float': return 'number';
    case 'Date': return 'date as YYYY-MM-DD or DD-MM-YYYY';
    case 'Boolean': return 'Yes or No';
    case 'Multi Select': return `one or more of ${(f.options || []).join(' / ')}, separated by commas`;
    case 'Textarea': return `text, up to ${MAX_TEXTAREA_LEN} characters`;
    default: return `text, up to ${MAX_STRING_LEN} characters`;
  }
}

/* ---------- Fields added on a single contact (not part of a template) ---------- */

/**
 * One added field while the contact form is open: the same settings as a template field
 * (required, default, min / max, options) plus this contact's value.
 */
export interface ManualFieldDraft extends BuilderField {
  key: string;
  value: CustomValue;
  /** Saved field in a values-only form: its settings are fixed and it cannot be removed. */
  locked?: boolean;
}

/**
 * As saved on the contact (contact_group_members.custom_field_defs). Fields saved before the
 * settings existed have only name, type and options, and were always required.
 */
export type ManualFieldDef = Pick<CustomField, 'field_name' | 'field_type' | 'options'> & Partial<CustomField>;

let manualSeq = 0;
const nextKey = () => `manual-${++manualSeq}`;

export const blankValueFor = (type: CustomFieldType): CustomValue =>
  type === 'Boolean' ? false : type === 'Multi Select' ? [] : '';

export const emptyManualField = (): ManualFieldDraft => ({
  ...emptyBuilderField(),
  key: nextKey(),
  value: '',
});

/** Draft → field definition used for the value input and checks (settings not yet valid: none applied). */
export function manualToField(m: ManualFieldDraft): CustomField {
  const built = builderFieldToField(m, '');
  if ('field' in built) return built.field;
  return {
    field_name: m.field_name.trim(),
    field_type: m.field_type,
    is_required: m.is_required,
    field_default: null,
    field_min: null,
    field_max: null,
    options: m.field_type === 'Multi Select' ? splitOptions(m.options) : null,
  };
}

/** Added fields of a saved contact, with their saved values. */
export function manualFromContact(defs: unknown, saved?: Record<string, unknown> | null): ManualFieldDraft[] {
  if (!Array.isArray(defs)) return [];
  return (defs as ManualFieldDef[])
    .filter((d) => d && typeof d.field_name === 'string' && FIELD_TYPES.includes(d.field_type))
    .map((d) => {
      const field: CustomField = {
        field_name: d.field_name,
        field_type: d.field_type,
        is_required: d.is_required ?? true,
        field_default: d.field_default ?? null,
        field_min: d.field_min ?? null,
        field_max: d.field_max ?? null,
        options: d.options ?? null,
      };
      const value = initialCustomValues({ id: 0, name: '', fields: [field] }, saved)[d.field_name];
      return {
        ...templateFieldToBuilder(field),
        key: nextKey(),
        value: value ?? blankValueFor(d.field_type),
      };
    });
}

/** First problem in the added fields, or null. */
export function validateManualFields(
  manual: ManualFieldDraft[],
  template: CustomFieldTemplate | null | undefined,
): string | null {
  const templateCount = template?.fields.length || 0;
  if (templateCount + manual.length > MAX_CUSTOM_FIELDS) {
    return `At most ${MAX_CUSTOM_FIELDS} custom fields per contact (template + added fields)`;
  }
  const taken = new Set((template?.fields || []).map((f) => f.field_name.toLowerCase()));
  for (const [i, m] of manual.entries()) {
    const at = `Added field #${i + 1}`;
    const name = m.field_name.trim();
    if (!name) return `${at}: field name is required`;
    if (!FIELD_NAME_REGEX.test(name)) return `${at}: field name contains invalid characters`;
    const key = name.toLowerCase();
    if (RESERVED_FIELD_NAMES.has(key)) return `${at}: "${name}" is already a contact field`;
    if (taken.has(key)) return `${at}: "${name}" is already used`;
    taken.add(key);
    const built = builderFieldToField(m, at);
    if ('error' in built) return built.error;
    const field = built.field;
    if (m.field_type !== 'Boolean' && isBlank(m.value)) {
      if (field.is_required) return `${name}: enter a value`;
      continue;
    }
    const err = valueError(field, m.value);
    if (err) return `${name} ${err}`;
  }
  return null;
}

/**
 * Request body part for the template and the added fields.
 * sendEmptyManual: on edit, send manual_fields even when empty so removed fields are cleared.
 */
export function contactCustomPayload(
  template: CustomFieldTemplate | null | undefined,
  values: CustomValues,
  manual: ManualFieldDraft[],
  sendEmptyManual: boolean,
) {
  const base = customFieldsPayload(template, values) as {
    custom_field_template_id?: number;
    custom_fields?: Record<string, CustomFieldDefault | ''>;
  };
  if (!manual.length && !sendEmptyManual) return base;
  const custom_fields = { ...(base.custom_fields || {}) };
  const manual_fields = manual.map((m) => {
    const field = manualToField(m);
    // Blank sent as '' so the backend applies the default (or clears it), as for template fields.
    custom_fields[field.field_name] = field.field_type !== 'Boolean' && isBlank(m.value) ? '' : toStoredValue(field, m.value);
    return field;
  });
  return { ...base, custom_fields, manual_fields };
}
